// src/components/ProtectedRoute/Admin/Dialogs/Sections/SectionTaxonomy.jsx
import React from "react";
import { Box, Typography, Autocomplete, TextField } from "@mui/material";
import ScientificNameSelector from "../parts/ScientificNameSelector";

export default function SectionTaxonomy({
  form,
  handleChange,
  errors = {},
  ranks = [],
  allowedRanks = [],
  invalidTypeOptions = [],
  scientificNameData = [],
  refresh,
}) {
  const rankHelpIfErr = (isErr) => (isErr ? "This field is required." : "");
  const rankOptions = React.useMemo(() => {
    const merged = [...(allowedRanks || []), ...(ranks || []), form.current_rank, form.original_rank]
      .filter(Boolean)
      .map((r) => (typeof r === "string" ? r : String(r)));
    const seen = new Set();
    return merged.filter((r) => {
      const key = r.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allowedRanks, ranks, form.current_rank, form.original_rank]);
  const isInvalidRank = (r) => typeof r === "string" && r.toLowerCase() === "invalid";

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Taxonomy
      </Typography>

      {/* Rank: モバイル1列、SM以上2列、AutocompleteはfullWidth */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <Autocomplete
          options={rankOptions}
          value={form.current_rank || ""}
          onChange={(_, v) => handleChange("current_rank", v || null)}
          isOptionEqualToValue={(option, value) => option === value}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Current Rank *"
              required
              error={!!errors.currentRank}
              helperText={rankHelpIfErr(!!errors.currentRank)}
            />
          )}
        />

        <Autocomplete
          options={rankOptions}
          value={form.original_rank || ""}
          onChange={(_, v) => handleChange("original_rank", v || null)}
          isOptionEqualToValue={(option, value) => option === value}
          fullWidth
          renderInput={(params) => <TextField {...params} fullWidth label="Original Rank" />}
        />
      </Box>

      {isInvalidRank(form.current_rank) && (
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            options={invalidTypeOptions}
            value={form.current_invalid_type || null}
            onChange={(_, v) => handleChange("current_invalid_type", v || null)}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Current Invalid Type *"
                error={!!errors.currentInvalidType}
                helperText={errors.currentInvalidType ? "Select an invalid type." : ""}
              />
            )}
          />
          {form.current_invalid_type === "homonym" && (
            <Box sx={{ mt: 1.5 }}>
              <ScientificNameSelector
                label="Homonym with"
                valueId={form.current_invalid_homonym_with || ""}
                value={form.current_invalid_homonym_with || ""}
                onChange={(id) => handleChange("current_invalid_homonym_with", id || null)}
                scientificNameData={scientificNameData}
                onRefreshData={refresh}
              />
              {errors.currentInvalidHomonym ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  Select the senior homonym taxon.
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>
      )}

      {/* Parent縦並び・全幅 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Current Parent *
        </Typography>
        <ScientificNameSelector
          key={`cur-${form.id}-${form.current_parent ?? "null"}`} // 編集切替時に確実に反映
          label="Search by name / id"
          isRequired
          // 編集時に消えないよう現在値を確実に渡す（想定されうるprop名をすべてケア）
          value={form.current_parent || null}
          selectedId={form.current_parent || null}
          valueId={form.current_parent || null}
          initialValueId={form.current_parent || null}
          scientificNameData={scientificNameData}
          onChange={(id) => handleChange("current_parent", id || null)}
          onRefreshData={refresh}
        />
        {errors.current_parent ? (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
            This field is required.
          </Typography>
        ) : null}
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Original Parent
        </Typography>
        <ScientificNameSelector
          key={`org-${form.id}-${form.original_parent ?? "null"}`}
          label="Search by name / id"
          value={form.original_parent || null}
          selectedId={form.original_parent || null}
          valueId={form.original_parent || null}
          initialValueId={form.original_parent || null}
          scientificNameData={scientificNameData}
          onChange={(id) => handleChange("original_parent", id || null)}
          onRefreshData={refresh}
        />
      </Box>

      {isInvalidRank(form.original_rank) && (
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            options={invalidTypeOptions}
            value={form.original_invalid_type || null}
            onChange={(_, v) => handleChange("original_invalid_type", v || null)}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Original Invalid Type"
                error={!!errors.originalInvalidType}
                helperText={errors.originalInvalidType ? "Select an invalid type." : ""}
              />
            )}
          />
          {form.original_invalid_type === "homonym" && (
            <Box sx={{ mt: 1.5 }}>
              <ScientificNameSelector
                label="Homonym with"
                valueId={form.original_invalid_homonym_with || ""}
                value={form.original_invalid_homonym_with || ""}
                onChange={(id) => handleChange("original_invalid_homonym_with", id || null)}
                scientificNameData={scientificNameData}
                onRefreshData={refresh}
              />
              {errors.originalInvalidHomonym ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  Select the senior homonym taxon.
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>
      )}

      <TextField
        label="Taxonomic Note"
        value={form.taxonomic_note || ""}
        onChange={(e) => handleChange("taxonomic_note", e.target.value)}
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 3 }}
      />
    </Box>
  );
}
